import { Button } from '@patchbay/ui'
import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { ArrowLeft, Pause, Play, SkipForward } from 'lucide-react'
import * as React from 'react'
import { ApiError, describeError, unwrap } from '../../lib/api/client'
import type { ChatThreadOut, LaneWalkSessionOut } from '../../lib/api/generated'
import {
  walkthroughBackApiV1WalkthroughsSessionIdBackPost,
  walkthroughBriefApiV1WalkthroughsSessionIdBriefPost,
  walkthroughCurrentApiV1WalkthroughsCurrentGet,
  walkthroughPauseApiV1WalkthroughsSessionIdPausePost,
  walkthroughResumeApiV1WalkthroughsSessionIdResumePost,
  walkthroughSkipApiV1WalkthroughsSessionIdSkipPost,
} from '../../lib/api/generated'
import { useWorkspace } from '../../lib/workspace'
import { useMe } from '../auth/session'
import { setChatThread } from './chat-store'
import type { RileyChatThread } from './contracts'
import { caseChatKey } from './rail'
import { WALKTHROUGH_EVENT, type WalkthroughEventDetail } from './walkthrough-event'

export const WALKTHROUGH_QUERY_KEY = ['riley', 'walkthrough', 'current'] as const

export const walkthroughQuery = () =>
  queryOptions({
    queryKey: WALKTHROUGH_QUERY_KEY,
    queryFn: async ({ signal }) => unwrap(walkthroughCurrentApiV1WalkthroughsCurrentGet({ signal })),
    staleTime: 15_000,
    retry: 1,
  })

type Control = 'back' | 'pause' | 'resume' | 'skip'

async function controlWalkthrough(action: Control, sessionId: string): Promise<LaneWalkSessionOut> {
  const options = { path: { session_id: sessionId } }
  if (action === 'back') return unwrap(walkthroughBackApiV1WalkthroughsSessionIdBackPost(options))
  if (action === 'pause') return unwrap(walkthroughPauseApiV1WalkthroughsSessionIdPausePost(options))
  if (action === 'resume') return unwrap(walkthroughResumeApiV1WalkthroughsSessionIdResumePost(options))
  return unwrap(walkthroughSkipApiV1WalkthroughsSessionIdSkipPost(options))
}

function liveWalk(walk: LaneWalkSessionOut): LaneWalkSessionOut | null {
  return walk.state === 'completed' ? null : walk
}

/** Persistent progress and controls. It also delivers the once-per-position Riley brief on the opened case. */
export function WalkthroughBanner() {
  const me = useMe()
  const allowed = me?.role === 'manager' || me?.role === 'cover' || me?.role === 'tech'
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const ws = useWorkspace()
  const current = useQuery({ ...walkthroughQuery(), enabled: Boolean(allowed) })
  const walk = current.data ?? null
  const [busy, setBusy] = React.useState<Control | 'brief' | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const briefing = React.useRef(new Set<string>())

  const go = React.useCallback(
    (next: LaneWalkSessionOut | null | undefined) => {
      if (next?.state === 'active' && next.current_case?.href) navigate({ href: next.current_case.href })
    },
    [navigate],
  )

  React.useEffect(() => {
    const changed = (raw: Event) => {
      const event = raw as CustomEvent<WalkthroughEventDetail>
      if (event.detail.walkthrough !== undefined) {
        const next = event.detail.walkthrough ? liveWalk(event.detail.walkthrough) : null
        queryClient.setQueryData(WALKTHROUGH_QUERY_KEY, next)
        go(next)
        return
      }
      void queryClient.fetchQuery({ ...walkthroughQuery(), staleTime: 0 }).then(go)
    }
    globalThis.addEventListener?.(WALKTHROUGH_EVENT, changed)
    return () => globalThis.removeEventListener?.(WALKTHROUGH_EVENT, changed)
  }, [go, queryClient])

  React.useEffect(() => {
    if (walk?.state !== 'active' || walk.briefed || !walk.current_case || pathname !== walk.current_case.href) return
    const key = `${walk.id}:${walk.position}`
    if (briefing.current.has(key)) return
    briefing.current.add(key)
    setBusy('brief')
    setError(null)
    ws.openRail()
    void unwrap(walkthroughBriefApiV1WalkthroughsSessionIdBriefPost({ path: { session_id: walk.id } }))
      .then((thread: ChatThreadOut) => {
        setChatThread(caseChatKey(walk.current_case?.case_id ?? ''), thread as RileyChatThread)
        const refreshed = thread.walkthrough ? liveWalk(thread.walkthrough) : walk
        queryClient.setQueryData(WALKTHROUGH_QUERY_KEY, refreshed)
      })
      .catch((reason: unknown) => {
        briefing.current.delete(key)
        setError(`Riley could not brief this case: ${describeError(reason)}`)
      })
      .finally(() => setBusy(null))
  }, [pathname, queryClient, walk, ws])

  // A walk in progress must not vanish because its read failed (hard rule 10). A route that is not there (404/501)
  // is the feature being off, not a failure, so it stays silent like no walk.
  const featureOff = current.error instanceof ApiError && (current.error.notBuilt || current.error.status === 404)
  if (allowed && !walk && current.isError && !featureOff) {
    return (
      <div className="border-b border-warning/30 bg-warning-soft px-4 py-1.5 text-xs text-warning" data-banner="walkthrough-unreachable">
        <span role="status">Walkthrough state is unreachable: {describeError(current.error)} </span>
        <button type="button" className="cursor-pointer underline" onClick={() => void current.refetch()}>
          Retry
        </button>
      </div>
    )
  }
  if (!allowed || !walk) return null

  const run = (action: Control) => {
    setBusy(action)
    setError(null)
    void controlWalkthrough(action, walk.id)
      .then((answer) => {
        const next = liveWalk(answer)
        queryClient.setQueryData(WALKTHROUGH_QUERY_KEY, next)
        go(next)
      })
      .catch((reason: unknown) => setError(describeError(reason)))
      .finally(() => setBusy(null))
  }

  return (
    <div className="border-b border-riley/20 bg-riley-soft px-4 py-2 text-sm" data-banner="walkthrough">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-riley">{walk.source_label}</span>
        <span className="tabular-nums text-muted-foreground">
          {walk.position}/{walk.total}
        </span>
        {walk.state === 'paused' ? <span className="text-muted-foreground">Paused — resume later from Home.</span> : null}
        {walk.state === 'active' && walk.current_case ? (
          <button type="button" className="mr-auto cursor-pointer underline-offset-2 hover:underline" onClick={() => go(walk)}>
            {walk.current_case.label}
          </button>
        ) : (
          <span className="mr-auto" />
        )}
        <Button size="sm" variant="ghost" disabled={!walk.can_back || busy !== null || walk.state !== 'active'} onClick={() => run('back')}>
          <ArrowLeft /> Back
        </Button>
        {walk.state === 'paused' ? (
          <Button size="sm" variant="primary" disabled={busy !== null} onClick={() => run('resume')}>
            <Play /> Resume
          </Button>
        ) : (
          <Button size="sm" variant="ghost" disabled={busy !== null} onClick={() => run('pause')}>
            <Pause /> Pause
          </Button>
        )}
        <Button size="sm" variant="ghost" disabled={busy !== null || walk.state !== 'active'} onClick={() => run('skip')}>
          <SkipForward /> Skip
        </Button>
      </div>
      {busy === 'brief' ? <p className="mt-1 text-xs text-muted-foreground">Riley is reviewing this case…</p> : null}
      {error ? (
        <p role="alert" className="mt-1 text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  )
}
